import { formatIQD, formatDateTime } from '../../../shared/format.js';
import { ReceiptBrand } from '../../../shared/ReceiptBrand.jsx';
import { SOURCE_LABELS, POS_TYPE_LABELS } from '../../../shared/useShift.js';

function durationLabel(from, to) {
  if (!from) return '—';
  const ms = new Date(to || Date.now()).getTime() - new Date(from).getTime();
  const min = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h} س ${m} د` : `${m} د`;
}

function Row({ label, value, bold, color }) {
  return (
    <div className="flex justify-between" style={{ padding: '1px 0' }}>
      <span>{label}</span>
      <span style={{ fontWeight: bold ? 800 : 600, color }}>{value}</span>
    </div>
  );
}

/**
 * «ملخص شفت كاشير» بعرض 80مم — يُطبع عبر shared/printReceipt.js الذي يلتقط
 * العنصر ذا الصنف receipt-print-area.
 */
export function ShiftReceipt({ shift, movements = [] }) {
  if (!shift) return null;
  const s = shift.summary || {};
  const hidden = !!shift.summaryHidden;
  const variance = shift.variance;
  const isClosed = shift.status === 'closed';

  return (
    <div
      className="receipt-print-area bg-white p-4"
      style={{ width: 280, fontFamily: '"Tajawal", monospace', fontSize: 12, borderRadius: 8 }}
    >
      <ReceiptBrand subtitle="ملخص شفت كاشير" />
      <hr className="border-dashed my-1" />
      <Row label="رقم الشفت:" value={String(shift._id || shift.id || '').slice(-6).toUpperCase()} />
      <Row label="الكاشير:" value={shift.cashierName || '—'} />
      <Row label="الدرج:" value={shift.cashboxName || '—'} />
      <Row label="الفتح:" value={formatDateTime(shift.openedAt)} />
      <Row label="الإغلاق:" value={isClosed ? formatDateTime(shift.closedAt) : 'مفتوح'} />
      <Row label="المدة:" value={durationLabel(shift.openedAt, shift.closedAt)} />
      {shift.closedByName && shift.closedByName !== shift.cashierName ? (
        <Row label="أقفله:" value={shift.closedByName} />
      ) : null}

      <hr className="border-dashed my-1" />
      <div className="text-center font-bold">تسوية الصندوق</div>
      <Row label="العهدة عند الفتح:" value={formatIQD(shift.openingBalance || 0)} />
      {shift.openingCarry?.mismatch ? (
        <div style={{ color: '#B45309', fontSize: 11 }}>⚠ عدم مطابقة الدرج عند الفتح</div>
      ) : null}
      {!hidden ? <Row label="النقد المتوقع:" value={formatIQD(shift.expectedCash || 0)} /> : null}
      {isClosed ? <Row label="النقد المعدود:" value={formatIQD(shift.countedCash || 0)} bold /> : null}
      {isClosed && !hidden && variance != null ? (
        <Row
          label="الفرق:"
          value={variance === 0 ? 'مطابق ✓' : `${variance > 0 ? '+' : ''}${formatIQD(variance)}`}
          bold
          color={variance === 0 ? '#15803D' : variance > 0 ? '#1D4ED8' : '#B91C1C'}
        />
      ) : null}
      {isClosed && Number(shift.carriedCash || 0) > 0 ? (
        <Row label="🔄 مدوّر للشفت التالي:" value={formatIQD(shift.carriedCash)} />
      ) : null}
      {isClosed && Number(shift.depositAmount || 0) > 0 ? (
        <Row label={`مُرحَّل إلى ${shift.depositCashboxName || 'القاصة'}:`} value={formatIQD(shift.depositAmount)} />
      ) : null}
      {shift.varianceApproved ? <div style={{ color: '#15803D', fontSize: 11 }}>✓ الفرق معتمد</div> : null}

      {!hidden ? (
        <>
          <hr className="border-dashed my-1" />
          <div className="text-center font-bold">حركات النقد (الدرج)</div>
          {(s.bySource || []).length ? (
            (s.bySource || []).map((b) => (
              <Row
                key={b.source}
                label={`${SOURCE_LABELS[b.source] || b.source} (${b.count})`}
                value={b.cashIn ? `+${formatIQD(b.cashIn)}` : `−${formatIQD(b.cashOut)}`}
              />
            ))
          ) : (
            <div className="text-center" style={{ color: '#6B7280' }}>لا حركات نقدية</div>
          )}
          <Row label="إجمالي الوارد:" value={formatIQD(s.cashIn || 0)} bold />
          <Row label="إجمالي الصادر:" value={formatIQD(s.cashOut || 0)} bold />

          {(s.byPosType || []).length ? (
            <>
              <hr className="border-dashed my-1" />
              <div className="text-center font-bold">الفواتير ({s.invoicesCount || 0})</div>
              {s.byPosType.map((p) => (
                <Row
                  key={p.posType}
                  label={`${POS_TYPE_LABELS[p.posType] || p.posType} (${p.count})`}
                  value={formatIQD(p.total)}
                />
              ))}
              {Number(s.managerDiscount || 0) > 0 ? (
                <Row label="خصومات إدارية:" value={`−${formatIQD(s.managerDiscount)}`} />
              ) : null}
            </>
          ) : null}

          {(s.cashOutItems || []).length ? (
            <>
              <hr className="border-dashed my-1" />
              <div className="text-center font-bold">المصروفات والسحوبات</div>
              {s.cashOutItems.map((m, i) => (
                <div key={i} style={{ fontSize: 11 }}>
                  <Row label={SOURCE_LABELS[m.source] || m.source} value={formatIQD(m.amount)} />
                  {m.note ? <div style={{ color: '#6B7280' }}>{m.note}</div> : null}
                </div>
              ))}
            </>
          ) : null}
        </>
      ) : (
        <div className="text-center mt-1" style={{ color: '#6B7280', fontSize: 11 }}>
          العدّ الأعمى مُفعّل — التفاصيل للمدير فقط
        </div>
      )}

      {shift.closeNotes ? (
        <>
          <hr className="border-dashed my-1" />
          <div style={{ fontSize: 11 }}>ملاحظات: {shift.closeNotes}</div>
        </>
      ) : null}

      <hr className="border-dashed my-1" />
      <Row label="توقيع الكاشير:" value="__________" />
      <Row label="توقيع المشرف:" value="__________" />
      <div className="text-center" style={{ fontSize: 10, marginTop: 4 }}>
        طُبع في {formatDateTime(new Date())}
        {movements.length ? ` • ${movements.length} حركة` : ''}
      </div>
    </div>
  );
}
